import { palette } from "@minervaproject/fds";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { addMinutes, format, formatISO, isValid, subMinutes } from "date-fns";
import React, { useMemo, useState } from "react";

import { Filter } from "./filter";
import { MonitoringEventHistogram } from "./monitoring_event_histogram";
import { TD, TH } from "./table";

import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useUserProfileProperties } from "hooks/use_user_properties";
import { AppDispatch } from "pubsub/client";
import { LevelType, MonitoringEventType } from "shared/types/monitoring_event";
import { MONITORING_EVENT_TYPES } from "shared/utils/monitoring";
import {
  setEndTimeFilter,
  setMonitoringEventTableEventsPerPage,
  setMonitoringEventTableLevels,
  setMonitoringEventTableTypes,
  setMonitoringEventTableUsers,
  setStartTimeFilter,
} from "store/reducers/local_monitoring";
import { selectUserDropdownOptions } from "store/selectors/global";
import {
  selectMonitoringEventTableEventsPerPage,
  selectMonitoringEventTableLevels,
  selectMonitoringEventTableTypes,
  selectMonitoringEventTableUsers,
} from "store/selectors/local_monitoring";
import {
  selectMonitoringEventCount,
  selectPaginatedMonitoringEvents,
} from "store/selectors/monitoring";
import { longDisplayName } from "utils/user";

const formatTime = (date: Date, full = false) => {
  if (!isValid(date)) return "-";
  return full ? formatISO(date) : format(date, "h:mm a xxx");
};

type MonitoringEventHeaderProps = {
  showUserId?: boolean;
};

function MonitoringEventHeader({ showUserId = true }: MonitoringEventHeaderProps) {
  return (
    <thead>
      <tr>
        <TH>Time</TH>
        {showUserId ? <TH>User ID</TH> : null}
        {showUserId ? <TH>Name</TH> : null}
        <TH>Event Level</TH>
        <TH>Event Type</TH>
        {/* <TH title="User ID / Target User ID (if these two differ)">User/Target User</TH> */}
        <TH>Details</TH>
      </tr>
    </thead>
  );
}

type MonitoringEventBodyProps = {
  showUserId?: boolean;
  paginatedMonitoringEvents: MonitoringEventType[];
};

function MonitoringEventBody({ paginatedMonitoringEvents, showUserId }: MonitoringEventBodyProps) {
  return (
    <tbody style={{ color: palette.blackTint20 }}>
      {paginatedMonitoringEvents.map((row) => (
        <MonitoringEventRow data={row} key={row.id} showUserId={showUserId} />
      ))}
    </tbody>
  );
}

type MonitoringEventRowProps = {
  showUserId?: boolean;
  data: MonitoringEventType;
};

function MonitoringEventRow({ data, showUserId = true }: MonitoringEventRowProps) {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const { timestamp, userId, contextData, type } = data;
  const level = MONITORING_EVENT_TYPES[type].level;
  //   const classId = data.klass_id;
  const user = useUserProfileProperties(userId);
  const expandedContextDataString = JSON.stringify(contextData, null, 2);
  const contextDataString = isOpen ? expandedContextDataString : JSON.stringify(data.contextData);

  //   const description =
  //     (EVENT_TYPES[data.type] && EVENT_TYPES[data.type].description) ||
  //     "No description found for this event";
  //   const level = (EVENT_TYPES[data.type] && EVENT_TYPES[data.type].level) || "?";
  //   const eventUniqueId = `monitoring_event_${data.id}`;

  const seeSurroundingEvents: React.MouseEventHandler<HTMLSpanElement> = () => {
    const createdOn = new Date(timestamp);
    const momentBefore = formatISO(subMinutes(createdOn, 2));
    const momentAfter = formatISO(addMinutes(createdOn, 2));
    dispatch(setStartTimeFilter(momentBefore));
    dispatch(setEndTimeFilter(momentAfter));
  };

  return (
    <tr
      className="pointer"
      style={{ lineHeight: isOpen ? 1.2 : 1 }}
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains("clickable-cell")) return;
        setIsOpen(!isOpen);
      }}
    >
      <TD noWrap>
        <>
          <span
            className="mx2 clickable-cell cursor-pointer"
            role="img"
            aria-label="Surrounding monitoring events"
            data-created-on={timestamp}
            onClick={seeSurroundingEvents}
          >
            🕐
          </span>
          {formatTime(new Date(timestamp), true)}
        </>
      </TD>
      {showUserId ? <TD>{userId}</TD> : null}
      {showUserId ? <TD noWrap>{user && longDisplayName(user)}</TD> : null}
      <TD noWrap>{level}</TD>
      <TD noWrap>{type}</TD>
      {/* <TD>
        {data.user_id === data.target_user_id ? "-/-" : `${data.user_id}/${data.target_user_id}`}
      </TD> */}
      <TD>
        <pre
          className="clickable-cell my0"
          onClick={(event) => {
            // Let's copy the data to the clipboard
            navigator.clipboard.writeText(expandedContextDataString);
            const target = event.target as HTMLElement;
            const oldText = target.innerText;
            target.innerText = "Copied";
            setTimeout(() => {
              target.innerText = oldText;
            }, 2000);
          }}
        >
          {contextDataString}
        </pre>
      </TD>
    </tr>
  );
}

type MonitoringEventFooterProps = {
  nPaginatedMonitoringEvents: number;
};

function MonitoringEventFooter({ nPaginatedMonitoringEvents }: MonitoringEventFooterProps) {
  const dispatch = useAppDispatch();
  const eventsPerPage = useAppSelector(selectMonitoringEventTableEventsPerPage());
  const selectedLevels = useAppSelector(selectMonitoringEventTableLevels());
  const selectedTypes = useAppSelector(selectMonitoringEventTableTypes());
  const selectedUsers = useArraySelector(selectMonitoringEventTableUsers());
  const nMonitoringEvents = useAppSelector(
    selectMonitoringEventCount(selectedUsers, selectedLevels, selectedTypes),
  );

  const handleShowMore = () => {
    dispatch(setMonitoringEventTableEventsPerPage(eventsPerPage + 250));
  };
  return (
    <div className="mt4 center body-s" data-testid="monitoring-events-count">
      Showing {nPaginatedMonitoringEvents} results out of {nMonitoringEvents}
      {nPaginatedMonitoringEvents !== nMonitoringEvents ? (
        <a className="ml1" onClick={handleShowMore}>
          Show more
        </a>
      ) : null}
    </div>
  );
}

const handleFilterChange =
  (
    allValues: string[],
    selectedValues: string[],
    setSelectedValues: ActionCreatorWithPayload<string[], string>,
    dispatch: AppDispatch,
  ) =>
  (value: string) => {
    if (value === "all") {
      if (!selectedValues.includes("all")) {
        dispatch(setSelectedValues([...allValues, "all"]));
      }
    } else if (selectedValues.includes(value)) {
      dispatch(setSelectedValues(selectedValues.filter((v) => v !== value && v !== "all")));
    } else {
      const newValues = [...selectedValues, value];
      if (selectedValues.length === allValues.length - 1) {
        newValues.push("all");
      }
      dispatch(setSelectedValues(newValues));
    }
  };

const LEVEL_OPTIONS = [LevelType.IMPORTANT, LevelType.INFO, LevelType.STATS].map((level) => ({
  label: level,
  value: level,
}));

function MonitoringEventTable() {
  const dispatch = useAppDispatch();

  const selectedLevels = useArraySelector(selectMonitoringEventTableLevels());
  const [levelInput, setLevelInput] = useState("");

  const typeOptions = useMemo(
    () =>
      (Object.keys(MONITORING_EVENT_TYPES) as Array<MonitoringEventType["type"]>)
        .filter(
          (key) =>
            selectedLevels.includes("all") ||
            selectedLevels.includes(MONITORING_EVENT_TYPES[key].level),
        )
        .map((key) => ({
          label: key,
          value: key,
        })),
    [selectedLevels],
  );

  const selectedTypes = useArraySelector(selectMonitoringEventTableTypes());
  const [typeInput, setTypeInput] = useState("");

  const userOptions = useArraySelector(selectUserDropdownOptions(), ["value"]);
  const selectedUsers = useArraySelector(selectMonitoringEventTableUsers());
  const [userInput, setUserInput] = useState("");

  const paginatedMonitoringEvents = useArraySelector(selectPaginatedMonitoringEvents(), ["id"]);

  return (
    <div data-testid="monitoring-event-table">
      <div className="flex justify-content-start pb3" style={{ flexWrap: "wrap" }}>
        <Filter
          title="Levels"
          filterKey="level"
          width={200}
          options={LEVEL_OPTIONS}
          selected={selectedLevels}
          input={levelInput}
          handleFilterChange={handleFilterChange(
            LEVEL_OPTIONS.map(({ value }) => value),
            selectedLevels,
            setMonitoringEventTableLevels,
            dispatch,
          )}
          handleClickOnly={(value) => dispatch(setMonitoringEventTableLevels([value]))}
          handleTextInputChange={(value) => setLevelInput(value)}
          clearInput={() => setLevelInput("")}
        />
        <Filter
          title="Types"
          filterKey="type"
          width={400}
          options={typeOptions}
          selected={selectedTypes}
          input={typeInput}
          handleFilterChange={handleFilterChange(
            typeOptions.map(({ value }) => value),
            selectedTypes,
            setMonitoringEventTableTypes,
            dispatch,
          )}
          handleClickOnly={(value) => dispatch(setMonitoringEventTableTypes([value]))}
          handleTextInputChange={(value) => setTypeInput(value)}
          clearInput={() => setTypeInput("")}
        />
        <Filter
          title="Users"
          filterKey="user"
          width={400}
          options={userOptions}
          selected={selectedUsers}
          input={userInput}
          handleFilterChange={handleFilterChange(
            userOptions.map(({ value }) => value),
            selectedUsers,
            setMonitoringEventTableUsers,
            dispatch,
          )}
          handleClickOnly={(value) => dispatch(setMonitoringEventTableUsers([value]))}
          handleTextInputChange={(value) => setUserInput(value)}
          clearInput={() => setUserInput("")}
        />
      </div>
      <MonitoringEventHistogram
        selectedLevels={selectedLevels}
        selectedTypes={selectedTypes}
        selectedUsers={selectedUsers}
      />
      <div
        className="overflow-hidden"
        style={{
          width: "calc(100vw - 48px)",
        }}
      >
        <table
          className="fds-table table-hover"
          style={{ tableLayout: "auto" }}
          data-testid="monitoring-events-table"
        >
          <MonitoringEventHeader />
          <MonitoringEventBody paginatedMonitoringEvents={paginatedMonitoringEvents} />
        </table>
      </div>
      <MonitoringEventFooter nPaginatedMonitoringEvents={paginatedMonitoringEvents.length} />
    </div>
  );
}

export {
  MonitoringEventHeader,
  MonitoringEventBody,
  MonitoringEventRow,
  MonitoringEventFooter,
  MonitoringEventTable,
};
